import type { KeyboardEvent, ReactNode } from 'react'
import { useMemo, useRef, useState } from 'react'
import { EmptyState } from './EmptyState'
import styles from './DataTable.module.css'

export type SortDirection = 'asc' | 'desc'

export interface Column<T> {
  key: string
  header: ReactNode
  /** Cell content. */
  render: (row: T) => ReactNode
  /** Value used for sorting. Omit to make the column unsortable. */
  sortValue?: (row: T) => string | number | null
  align?: 'left' | 'right' | 'center'
  /** CSS width, e.g. 60 or '20%'. */
  width?: number | string
  /** Numeric columns use tabular mono figures. */
  numeric?: boolean
  /** Hide below this viewport width (px) to keep phones readable. */
  hideBelow?: number
  /** In the stacked phone layout: take a full row of its own (e.g. the headline). */
  stackWide?: boolean
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  /** Visible or screen-reader caption. */
  caption: string
  showCaption?: boolean
  /** Enter / click on a row. */
  onRowActivate?: (row: T) => void
  density?: 'dense' | 'regular'
  /** Below 480 px render each row as a stacked card instead of a sideways-scrolling table. */
  stackOnMobile?: boolean
  /** Scroll height for the sticky header; omit to size to content. */
  maxHeight?: number | string
  initialSort?: { key: string; direction: SortDirection }
  /** Controlled sort (e.g. server-side); pair with onSortChange. */
  sort?: { key: string; direction: SortDirection } | null
  onSortChange?: (sort: { key: string; direction: SortDirection }) => void
  state?: 'ready' | 'loading' | 'empty' | 'not-connected' | 'error'
  emptyTitle?: string
  emptyBody?: ReactNode
  footer?: ReactNode
  /** Row ids to highlight (e.g. newly streamed rows). */
  highlight?: ReadonlySet<string | number>
}

/** Compare for the given direction; missing values stay last either way. */
function compare(a: string | number | null, b: string | number | null, direction: SortDirection): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  const c = typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), 'en-IN', { numeric: true, sensitivity: 'base' })
  return direction === 'asc' ? c : -c
}

/**
 * Dense data table: real <table> semantics, sticky header, sortable columns
 * (aria-sort), and keyboard row navigation — ↑/↓, Home/End, PageUp/PageDown
 * move between rows; Enter activates the focused row.
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns, rows, rowKey, caption, showCaption = false, onRowActivate, density = 'dense', stackOnMobile = false, maxHeight,
    initialSort, sort: controlledSort, onSortChange, state = 'ready', emptyTitle = 'Nothing to show',
    emptyBody, footer, highlight,
  } = props
  const [localSort, setLocalSort] = useState(initialSort ?? null)
  const sort = controlledSort !== undefined ? controlledSort : localSort
  const [active, setActive] = useState(0)
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([])

  const sorted = useMemo(() => {
    if (!sort || controlledSort !== undefined) return rows
    const col = columns.find(c => c.key === sort.key)
    if (!col?.sortValue) return rows
    const get = col.sortValue
    return [...rows].sort((a, b) => compare(get(a), get(b), sort.direction))
  }, [rows, sort, columns, controlledSort])

  const toggleSort = (key: string) => {
    const direction: SortDirection = sort?.key === key && sort.direction === 'desc' ? 'asc' : 'desc'
    const nextSort = { key, direction }
    if (onSortChange) onSortChange(nextSort)
    if (controlledSort === undefined) setLocalSort(nextSort)
  }

  const moveTo = (i: number) => {
    const clamped = Math.max(0, Math.min(sorted.length - 1, i))
    setActive(clamped)
    rowRefs.current[clamped]?.focus()
  }

  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, i: number, row: T) => {
    const page = 10
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); moveTo(i + 1); break
      case 'ArrowUp': e.preventDefault(); moveTo(i - 1); break
      case 'Home': e.preventDefault(); moveTo(0); break
      case 'End': e.preventDefault(); moveTo(sorted.length - 1); break
      case 'PageDown': e.preventDefault(); moveTo(i + page); break
      case 'PageUp': e.preventDefault(); moveTo(i - page); break
      case 'Enter':
        if (onRowActivate && e.target === e.currentTarget) { e.preventDefault(); onRowActivate(row) }
        break
    }
  }

  const colStyle = (c: Column<T>) => (c.width !== undefined ? { width: c.width } : undefined)
  const hideClass = (c: Column<T>) => (c.hideBelow ? styles[`hide${c.hideBelow}`] ?? '' : '')
  const activeIndex = Math.min(active, Math.max(0, sorted.length - 1))

  const body = (() => {
    if (state === 'loading') {
      return Array.from({ length: 6 }, (_, r) => (
        <tr key={`sk-${r}`} className={styles.row} aria-hidden="true">
          {columns.map(c => <td key={c.key} className={`${styles.cell} ${hideClass(c)}`}><span className={styles.bone} /></td>)}
        </tr>
      ))
    }
    if (state !== 'ready' || sorted.length === 0) return null
    return sorted.map((row, i) => {
      const id = rowKey(row)
      return (
        <tr
          key={id}
          ref={el => { rowRefs.current[i] = el }}
          tabIndex={i === activeIndex ? 0 : -1}
          className={`${styles.row} ${onRowActivate ? styles.clickable : ''} ${highlight?.has(id) ? styles.fresh : ''}`}
          onKeyDown={e => onRowKey(e, i, row)}
          onFocus={() => setActive(i)}
          onClick={e => {
            if (!onRowActivate) return
            if ((e.target as HTMLElement).closest('a,button,input,select')) return
            onRowActivate(row)
          }}
        >
          {columns.map(c => (
            <td key={c.key} className={`${styles.cell} ${styles[c.align ?? 'left']} ${c.numeric ? styles.numeric : ''} ${hideClass(c)} ${c.stackWide ? styles.stackWide : ''}`}>
              {c.render(row)}
            </td>
          ))}
        </tr>
      )
    })
  })()

  return (
    <div className={`${styles.wrap} ${styles[density]} ${stackOnMobile ? styles.stack : ''}`}>
      {/* Focusable so keyboard users can scroll it when there are no focusable rows inside. */}
      <div
        className={styles.scroll}
        style={maxHeight !== undefined ? { maxHeight } : undefined}
        tabIndex={state === 'ready' && sorted.length > 0 ? undefined : 0}
      >
        <table className={styles.table} aria-busy={state === 'loading' || undefined}>
          <caption className={showCaption ? styles.caption : 'sr-only'}>
            {caption}{sorted.length > 0 && state === 'ready' ? <span className="sr-only">. Use arrow keys to move between rows.</span> : null}
          </caption>
          <thead>
            <tr>
              {columns.map(c => {
                const sortable = !!c.sortValue
                const dir = sort?.key === c.key ? sort.direction : undefined
                return (
                  <th
                    key={c.key}
                    scope="col"
                    style={colStyle(c)}
                    className={`${styles.th} ${styles[c.align ?? 'left']} ${hideClass(c)}`}
                    aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : sortable ? 'none' : undefined}
                  >
                    {sortable ? (
                      <button type="button" className={styles.sortBtn} onClick={() => toggleSort(c.key)}>
                        {c.header}
                        <span aria-hidden="true" className={`${styles.sortIcon} ${dir ? styles.sortActive : ''}`}>
                          {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕'}
                        </span>
                      </button>
                    ) : c.header}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {/* Empty states sit outside the scroller so they stay readable on narrow screens. */}
      {state !== 'loading' && (state !== 'ready' || sorted.length === 0) && (
        <EmptyState
          kind={state === 'not-connected' ? 'not-connected' : state === 'error' ? 'error' : 'empty'}
          title={state === 'not-connected' ? 'Data not connected' : state === 'error' ? 'Could not load data' : emptyTitle}
          compact
        >
          {emptyBody}
        </EmptyState>
      )}
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  )
}
